// Croatian Public Holidays
// Includes fixed dates and calculated holidays (Easter-based)

import { format, isValid } from 'date-fns';

// Fixed Croatian holidays (same date every year) - Updated 2025
const FIXED_HOLIDAYS = [
  { month: 1, day: 1, name: { hr: 'Nova godina', en: 'New Year\'s Day' } },
  { month: 1, day: 6, name: { hr: 'Bogojavljenje', en: 'Epiphany' } },
  { month: 5, day: 1, name: { hr: 'Praznik rada', en: 'Labour Day' } },
  { month: 5, day: 30, name: { hr: 'Dan državnosti', en: 'Statehood Day' } },
  { month: 6, day: 22, name: { hr: 'Dan antifašističke borbe', en: 'Anti-Fascist Struggle Day' } },
  { month: 8, day: 5, name: { hr: 'Dan domovinske zahvalnosti', en: 'Victory and Homeland Thanksgiving Day' } },
  { month: 8, day: 15, name: { hr: 'Velika Gospa', en: 'Assumption of Mary' } },
  { month: 11, day: 1, name: { hr: 'Dan svih svetih', en: 'All Saints\' Day' } },
  { month: 11, day: 18, name: { hr: 'Dan sjećanja na žrtve Domovinskog rata', en: 'Remembrance Day for Homeland War Victims' } },
  { month: 12, day: 25, name: { hr: 'Božić', en: 'Christmas Day' } },
  { month: 12, day: 26, name: { hr: 'Sveti Stjepan', en: 'St. Stephen\'s Day' } }
];

// Calculate Easter Sunday for a given year using the Western (Gregorian) calendar
function calculateEaster(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month - 1, day);
}

// Calculate Easter-based holidays for a given year
function getEasterBasedHolidays(year) {
  const easter = calculateEaster(year);
  const holidays = [];
  
  // Easter Sunday
  holidays.push({
    date: new Date(easter),
    name: { hr: 'Uskrs', en: 'Easter Sunday' }
  });
  
  // Easter Monday (day after Easter)
  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);
  holidays.push({
    date: easterMonday,
    name: { hr: 'Uskršnji ponedjeljak', en: 'Easter Monday' }
  });
  
  // Corpus Christi (60 days after Easter)
  const corpusChristi = new Date(easter);
  corpusChristi.setDate(easter.getDate() + 60);
  holidays.push({
    date: corpusChristi,
    name: { hr: 'Tijelovo', en: 'Corpus Christi' }
  });
  
  return holidays;
}

// Get all Croatian holidays for a specific year
export function getCroatianHolidays(year) {
  const holidays = [];
  
  // Add fixed holidays
  FIXED_HOLIDAYS.forEach(holiday => {
    const date = new Date(year, holiday.month - 1, holiday.day);
    if (isValid(date)) {
      holidays.push({
        date,
        name: holiday.name
      });
    }
  });
  
  // Add Easter-based holidays
  const easterHolidays = getEasterBasedHolidays(year);
  holidays.push(...easterHolidays);
  
  return holidays;
}

// Check if a specific date is a Croatian holiday
export function isCroatianHoliday(date) {
  if (!isValid(date)) return false;
  
  const year = date.getFullYear();
  const holidays = getCroatianHolidays(year);
  
  return holidays.some(holiday => {
    return format(holiday.date, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
  });
}

// Get holiday information for a specific date
export function getCroatianHolidayInfo(date, language = 'hr') {
  if (!isValid(date)) return null;
  
  const year = date.getFullYear();
  const holidays = getCroatianHolidays(year);
  
  const holiday = holidays.find(holiday => {
    return format(holiday.date, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
  });
  
  if (holiday) {
    return {
      name: holiday.name[language] || holiday.name['hr'],
      date: holiday.date
    };
  }
  
  return null;
}

// Get all holidays for a specific month and year
export function getCroatianHolidaysForMonth(year, month) {
  const holidays = getCroatianHolidays(year);
  
  return holidays.filter(holiday => {
    return holiday.date.getFullYear() === year && holiday.date.getMonth() === month;
  });
}
